import { defineStore } from "pinia";
import { computed, ref } from "vue";
import router from "../../router/index";
import i18n from "../../../i18n";
import { fetchJournalRowsByHash, type JournalRow } from "../../api/journalRowsApi";
import { hasActiveSession } from "../../common/helpers/auth/sessionAccess";
import {
  decidePlacementProgress,
  isOrderPlacementFailed,
  journalStatusCode,
  type PlacementProgress,
} from "../../common/helpers/eplex/journalOrderStatus";
import { journalRowsLocation } from "../../common/helpers/eplex/journalRowsLink";
import type { QuickCartItem } from "../../common/helpers/eplex/quickCartGrouping";
import { mapQuickCartItem } from "../../common/helpers/eplex/quickCartItemMapper";
import {
  placementFailureMessage,
  placementFailureReason,
  placementPollDelay,
  PLACEMENT_TIMEOUT_MS,
  type QuickCartPlacementFailure,
} from "../../common/helpers/eplex/quickCartPlacement";
import { loadQuickCartPlacements, saveQuickCartPlacements } from "../../common/helpers/eplex/quickCartPlacementStorage";
import { toText } from "../../common/helpers/toText";
import { useNotifications } from "../useNotifications";

/**
 * Судьба заказов, отправленных из Быстрой корзины (CAB-782).
 *
 * `make_order` лишь принимает заявку: заказ у поставщика создаётся асинхронно, и
 * отказ (лимит по сумме, незаполненные реквизиты, поставщик не принял) приходит
 * позже — сменой статуса строки журнала на «Оформлено · Ошибка». Быстрая корзина
 * убирала позицию сразу после приёма и об отказе не узнавала: клиент считал, что
 * заказал, а заказа не было.
 *
 * Здесь принятые позиции опрашиваются в журнале по ключу до итога. Отказ
 * объявляется постоянным тостом с переходом в основную корзину на эти строки и
 * остаётся отмеченным в панели, пока строку не оформят повторно или не удалят.
 * Ожидания и отказы переживают перезагрузку: хранятся ключами, а данные строки
 * перечитываются из журнала.
 */

interface PendingPlacement {
  thash: string;
  progress: PlacementProgress;
}

const translate = (key: string, params?: Record<string, unknown>) => i18n.global.t(key, params ?? {});

function unique(values: Iterable<string>): string[] {
  return Array.from(new Set(values));
}

export const useQuickCartPlacementStore = defineStore("quickCartPlacement", () => {
  const stored = loadQuickCartPlacements();
  const pending = ref<PendingPlacement[]>(stored.pending.map(({ thash, ...progress }) => ({ thash, progress })));
  const failures = ref<QuickCartPlacementFailure[]>([]);
  // Отказы из хранилища, ещё не сверенные с журналом: показать их нечем (данных
  // строки нет), но и забыть нельзя — первое обновление их либо подтвердит, либо снимет
  let restoredFailed = new Set(stored.failed);

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let refreshing = false;

  const failedItems = computed(() => failures.value.map((failure) => failure.item));
  const hasPending = computed(() => pending.value.length > 0);

  function failureOf(thash: string): QuickCartPlacementFailure | null {
    return failures.value.find((failure) => failure.thash === thash) ?? null;
  }

  function trackedThashes(): string[] {
    return unique([
      ...pending.value.map((entry) => entry.thash),
      ...failures.value.map((f) => f.thash),
      ...restoredFailed,
    ]);
  }

  function persist() {
    saveQuickCartPlacements({
      pending: pending.value.map((entry) => ({ thash: entry.thash, ...entry.progress })),
      failed: unique([...failures.value.map((failure) => failure.thash), ...restoredFailed]),
    });
  }

  /** Заявка по позициям принята бэком: следим за ними до итога. */
  function track(items: readonly QuickCartItem[]) {
    if (!items.length) return;
    const now = Date.now();
    for (const item of items) {
      // повторная отправка той же позиции — прежний отказ снят, ждём заново
      failures.value = failures.value.filter((failure) => failure.thash !== item.thash);
      restoredFailed.delete(item.thash);
      const progress: PlacementProgress = { seenStatus: 0, seenAttempt: null, seenAt: now };
      const existing = pending.value.find((entry) => entry.thash === item.thash);
      if (existing) existing.progress = progress;
      else pending.value.push({ thash: item.thash, progress });
    }
    persist();
    schedulePoll(0);
  }

  /** Сверить всё отслеживаемое с журналом: и ожидания, и уже известные отказы. */
  async function refresh(): Promise<void> {
    const thashes = trackedThashes();
    if (!thashes.length || refreshing) return;
    if (!hasActiveSession()) {
      stopPolling();
      return;
    }

    refreshing = true;
    try {
      const rows = await fetchJournalRowsByHash(thashes);
      applyRows(new Map(rows.map((row) => [toText(row.hash_key ?? row.thash), row])));
    } catch (error) {
      console.error("Quick cart placement refresh error: ", error);
    } finally {
      refreshing = false;
    }
  }

  function applyRows(rows: Map<string, JournalRow>) {
    const now = Date.now();
    const fresh: QuickCartPlacementFailure[] = [];
    const stillPending: PendingPlacement[] = [];

    for (const entry of pending.value) {
      const row = rows.get(entry.thash);
      const decision = decidePlacementProgress(entry.progress, row, now, PLACEMENT_TIMEOUT_MS);
      if (decision.action === "wait") {
        stillPending.push(entry);
      } else if (decision.action === "advance") {
        const progress = { seenStatus: decision.seenStatus, seenAttempt: decision.seenAttempt, seenAt: now };
        stillPending.push({ thash: entry.thash, progress });
      } else if (decision.action === "resolve" && decision.outcome === "failed" && row) {
        fresh.push(failureFromRow(entry.thash, row));
      }
      // заказ создан или ждать дольше бессмысленно — молча забываем
    }

    pending.value = stillPending;
    failures.value = [...refreshKnownFailures(rows), ...fresh];
    restoredFailed = new Set();
    persist();
    if (fresh.length) reportFailures(fresh);
  }

  /**
   * Известные отказы сверяем с журналом: строку оформили повторно (статус ушёл
   * из ошибки) или удалили (строки нет) — отказ снят; иначе обновляем причину.
   */
  function refreshKnownFailures(rows: Map<string, JournalRow>): QuickCartPlacementFailure[] {
    const known = unique([...failures.value.map((failure) => failure.thash), ...restoredFailed]);
    return known.flatMap((thash) => {
      const row = rows.get(thash);
      return row && isOrderPlacementFailed(row) ? [failureFromRow(thash, row)] : [];
    });
  }

  function failureFromRow(thash: string, row: JournalRow): QuickCartPlacementFailure {
    return {
      thash,
      item: mapQuickCartItem(row),
      status: journalStatusCode(row.status) ?? 0,
      reason: placementFailureReason(row, translate),
    };
  }

  /** Отказы одного обновления — одним тостом: причины по позициям и переход к ним в журнале. */
  function reportFailures(fresh: readonly QuickCartPlacementFailure[]) {
    const thashes = fresh.map((failure) => failure.thash);
    useNotifications().notifySticky(placementFailureMessage(fresh, translate), "error", {
      label: translate("quickCart.goToCart"),
      handler: () => {
        router.push(journalRowsLocation(thashes));
      },
    });
  }

  function schedulePoll(delay: number) {
    stopPolling();
    pollTimer = setTimeout(runPollCycle, delay);
  }

  async function runPollCycle() {
    pollTimer = null;
    await refresh();
    if (!pending.value.length) return;
    schedulePoll(
      placementPollDelay(
        pending.value.map((entry) => entry.progress),
        Date.now(),
      ),
    );
  }

  function stopPolling() {
    if (pollTimer !== null) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }

  // ожидания, восстановленные после перезагрузки, продолжаем опрашивать сразу
  if (pending.value.length) schedulePoll(0);

  return {
    failures,
    failedItems,
    hasPending,
    failureOf,
    track,
    refresh,
    stopPolling,
  };
});
